import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Box, Button, CircularProgress, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import clockMetricIcon from '@/assets/images/clock-circle-new.svg';
import deleteIcon from '@/assets/images/trash-bin-trash.svg';
import editIcon from '@/assets/images/pen-2.svg';
import notebookMetricIcon from '@/assets/images/notebook-new.svg';
import responsesMetricIcon from '@/assets/images/verified-check.svg';
import assignedMetricIcon from '@/assets/images/users-group-rounded.svg';
import bookIcon from '@/assets/images/book.svg';
import type { QuestionnaireCardData } from './types';
import {
  getCardDescription,
  getCardEstimatedMinutes,
  getCardQuestionCount,
  getCardTitle,
} from './types';
import { getQuestionnaireAssignmentMeta } from './meta';
import { QuestionnaireAddIcon } from './QuestionnaireAddIcon';
import {
  QUICK_ACTION_TOOLTIP_MESSAGE,
  QUICK_ACTION_TOOLTIP_SLOT_PROPS,
} from './quickActionTooltip';

const listRowTitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '15px',
  lineHeight: '22.5px',
  letterSpacing: '-0.23px',
  color: '#0f172a',
  overflowWrap: 'anywhere',
} as const;

const listRowSubtitleSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.15px',
  color: '#667085',
  overflowWrap: 'anywhere',
} as const;

interface QuestionnaireListRowProps {
  card: QuestionnaireCardData;
  onPreview: () => void;
  onResponses: () => void;
  onAssign: () => void;
  onFillManually?: () => void;
  showDescription?: boolean;
  previewLabel?: string;
  responsesLabel?: string;
  fillManuallyLabel?: string;
  responsesButtonVariant?: 'outlined' | 'contained';
  assignButtonVariant?: 'outlined' | 'contained';
  assignLabel?: string;
  assignEndIcon?: ReactNode;
  disableResponses?: boolean;
  disableFillManually?: boolean;
  disableAssign?: boolean;
  showResponsesButton?: boolean;
  showFillManuallyButton?: boolean;
  showAssignButton?: boolean;
  showPreviewButton?: boolean;
  enablePreviewClick?: boolean;
  showStatusChip?: boolean;
  showQuestionMetric?: boolean;
  showEstimatedTimeMetric?: boolean;
  showAssignedMetric?: boolean;
  showResponsesMetric?: boolean;
  preferRawDescription?: boolean;
  titleStatusChip?: {
    label: string;
    bgColor?: string;
    color?: string;
    dotColor?: string;
  };
  onEdit?: () => void;
  onDelete?: () => void;
  variant?: 'default' | 'conditional';
  leadingIconSrc?: string;
  quickAction?: {
    ariaLabel: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
  };
}

function getListTimeLabel(card: QuestionnaireCardData): string {
  const n = getCardQuestionCount(card);
  if (n >= 70) return '25-35 min';
  if (n >= 40) return '10-15 min';
  if (n >= 20) return '5-10 min';
  return '3-5 min';
}

export function QuestionnaireListRow({
  card,
  onPreview,
  onResponses,
  onAssign,
  onFillManually,
  showDescription = true,
  previewLabel = 'Preview',
  responsesLabel = 'Responses',
  fillManuallyLabel = 'Fill Manually',
  responsesButtonVariant = 'outlined',
  assignButtonVariant = 'contained',
  assignLabel = 'Assign',
  assignEndIcon,
  disableResponses,
  disableFillManually,
  disableAssign,
  showResponsesButton = true,
  showFillManuallyButton = false,
  showAssignButton = true,
  showPreviewButton = true,
  enablePreviewClick = true,
  showStatusChip = true,
  showQuestionMetric = true,
  showEstimatedTimeMetric = true,
  showAssignedMetric = true,
  showResponsesMetric = true,
  preferRawDescription = false,
  titleStatusChip,
  onEdit,
  onDelete,
  variant = 'default',
  leadingIconSrc,
  quickAction,
}: QuestionnaireListRowProps) {
  const { isAssigned, assignedCount } = getQuestionnaireAssignmentMeta(card);
  const responseCount = card.responsesCount ?? 0;
  const totalQuestions = getCardQuestionCount(card);
  const cardDescription = getCardDescription(card).trim();
  const description = preferRawDescription
    ? cardDescription
    : card.type === 'custom'
      ? `${totalQuestions} ${totalQuestions === 1 ? 'question' : 'questions'}`
      : cardDescription;
  const isConditional = variant === 'conditional';
  const showDeleteAction = card.type === 'custom';
  const isConditionalModule = isConditional || card.isConditionalModule === true;
  const shouldShowQuestionMetric =
    showQuestionMetric && (isConditionalModule || card.type !== 'custom' || isAssigned);
  const shouldShowEstimatedTimeMetric =
    showEstimatedTimeMetric && (isConditionalModule || card.type !== 'custom');
  const isDraft = card.status === 'draft';
  const responsesDisabled = disableResponses ?? isDraft;
  const fillManuallyDisabled = disableFillManually ?? isDraft;
  const assignDisabled = disableAssign ?? isDraft;
  const statusChipLabel = card.status === 'draft' ? 'Draft' : isAssigned ? 'Assigned' : 'Not Assigned';
  const statusChipBg = card.status === 'draft' ? '#e0f2fe' : isAssigned ? '#f6ad0f' : '#e5e7eb';
  const statusChipColor = card.status === 'draft' ? '#0369a1' : isAssigned ? '#fff' : '#444';
  const statusChipDotColor = card.status === 'draft' ? '#0369a1' : isAssigned ? '#fff' : '#111827';
  const resolvedStatusChipLabel = titleStatusChip?.label ?? statusChipLabel;
  const resolvedStatusChipBg = titleStatusChip?.bgColor ?? statusChipBg;
  const resolvedStatusChipColor = titleStatusChip?.color ?? statusChipColor;
  const resolvedStatusChipDotColor = titleStatusChip?.dotColor ?? statusChipDotColor;
  const responsesButtonIsContained = responsesButtonVariant === 'contained';
  const assignButtonIsContained = assignButtonVariant === 'contained';
  const canPreviewFromRow = enablePreviewClick && typeof onPreview === 'function';
  const cardTitle = getCardTitle(card);
  
  // Custom fix to ensure responsesButtonIsContained looks like the screenshot (solid blue but disabled outline)
  // We'll adjust the styles below if it's "Send Reminder" and we want the default blue styles.

  return (
    <Paper
      elevation={0}
      onClick={canPreviewFromRow ? onPreview : undefined}
      role={canPreviewFromRow ? 'button' : undefined}
      tabIndex={canPreviewFromRow ? 0 : undefined}
      onKeyDown={
        canPreviewFromRow
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onPreview();
              }
            }
          : undefined
      }
      sx={{
        position: 'relative',
        borderRadius: '12px',
        border: '1px solid #dbe6eb',
        bgcolor: '#fff',
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 2.5 },
        cursor: canPreviewFromRow ? 'pointer' : 'default',
      }}
    >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { xs: 'stretch', lg: 'center' },
            justifyContent: 'space-between',
            gap: 3,
            minWidth: 0,
          }}
        >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'flex-start' },
            gap: { xs: 2, md: 2.25 },
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: isConditional ? 44 : 56,
              height: isConditional ? 44 : 56,
              borderRadius: isConditional ? '12px' : '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isConditional ? '#dff2fa' : '#e8f1fa',
              color: isConditional ? '#1699c8' : '#6b7280',
              flexShrink: 0,
              position: 'relative',
              top: 5,
            }}
          >
            <Box
              component="img"
              src={leadingIconSrc || bookIcon}
              alt=""
              sx={{ width: isConditional ? 24 : 26, height: isConditional ? 24 : 26, display: 'block' }}
            />
          </Box>

          <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                gap: isConditional ? 0.5 : 1.25,
                minWidth: 0,
                maxWidth: '100%',
              }}
            >
              <Tooltip title={cardTitle} placement="top" arrow>
                <Typography
                  component="span"
                  sx={{
                    ...listRowTitleSx,
                    minWidth: 0,
                    maxWidth: { xs: '100%', sm: 'min(100%, 42ch)' },
                    display: '-webkit-box',
                    WebkitLineClamp: { xs: 2, sm: 1 },
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {cardTitle}
                </Typography>
              </Tooltip>
              {!isConditional && showStatusChip && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <Box
                    component="span"
                    sx={{
                      height: 24,
                      maxWidth: '100%',
                      px: 1.25,
                      borderRadius: '999px',
                      bgcolor: resolvedStatusChipBg,
                      border: resolvedStatusChipBg === 'transparent' ? '1px solid #eaecf0' : '1px solid transparent',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      minWidth: 0,
                      flexShrink: 0,
                    }}
                  >
                    {resolvedStatusChipDotColor && resolvedStatusChipDotColor !== 'transparent' && (
                      <Box
                        component="span"
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: resolvedStatusChipDotColor,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Box
                      component="span"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: '12px',
                        lineHeight: '16px',
                        color: resolvedStatusChipColor,
                      }}
                    >
                      {resolvedStatusChipLabel}
                    </Box>
                  </Box>
                  {quickAction && (
                    <Tooltip
                      title={card.type === 'core' ? QUICK_ACTION_TOOLTIP_MESSAGE : ''}
                      placement="top"
                      arrow
                      disableHoverListener={card.type !== 'core'}
                      disableFocusListener={card.type !== 'core'}
                      disableTouchListener={card.type !== 'core'}
                      slotProps={QUICK_ACTION_TOOLTIP_SLOT_PROPS}
                    >
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        <IconButton
                          data-action-tour-id="questionnaires-add-custom"
                          onClick={(event) => {
                            event.stopPropagation();
                            if (quickAction.disabled || quickAction.loading) return;
                            quickAction.onClick();
                          }}
                          disabled={quickAction.disabled}
                          aria-label={quickAction.ariaLabel}
                          size="small"
                          sx={{
                            width: { xs: 44, sm: 36 },
                            height: { xs: 44, sm: 36 },
                            borderRadius: '14px',
                            bgcolor: 'transparent',
                            color: '#99A1AF',
                            '&:hover': { bgcolor: 'transparent' },
                          }}
                        >
                          {quickAction.loading ? (
                            <CircularProgress size={16} thickness={5} sx={{ color: '#99A1AF' }} />
                          ) : (
                            <Box
                              component="span"
                              aria-hidden="true"
                              sx={{
                                width: { xs: 24, sm: 22 },
                                height: { xs: 30, sm: 26 },
                                position: 'relative',
                                display: 'inline-flex',
                                transform: { xs: 'translate(1px, 4px)', sm: 'translate(1px, 4px)' },
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  position: 'absolute',
                                  left: { xs: 1, sm: 1 },
                                  top: { xs: -4, sm: -3 },
                                  width: { xs: 16, sm: 14 },
                                  height: { xs: 20, sm: 18 },
                                  borderRadius: '4px',
                                  bgcolor: '#B8BEC9',
                                }}
                              />
                              <Box
                                component="span"
                                sx={{
                                  position: 'absolute',
                                  right: 0,
                                  top: { xs: 2, sm: 1 },
                                  width: { xs: 18, sm: 16 },
                                  height: { xs: 20, sm: 18 },
                                  borderRadius: '4px',
                                  bgcolor: '#99A1AF',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <AddRoundedIcon sx={{ fontSize: { xs: 16, sm: 14 }, color: '#fff' }} />
                              </Box>
                            </Box>
                          )}
                        </IconButton>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              )}
              {isConditional && quickAction && (
                <Tooltip
                  title={card.type === 'core' ? QUICK_ACTION_TOOLTIP_MESSAGE : ''}
                  placement="top"
                  arrow
                  disableHoverListener={card.type !== 'core'}
                  disableFocusListener={card.type !== 'core'}
                  disableTouchListener={card.type !== 'core'}
                  slotProps={QUICK_ACTION_TOOLTIP_SLOT_PROPS}
                >
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex' }}
                  >
                    <IconButton
                      data-action-tour-id="questionnaires-add-custom"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (quickAction.disabled || quickAction.loading) return;
                        quickAction.onClick();
                      }}
                      disabled={quickAction.disabled}
                      aria-label={quickAction.ariaLabel}
                      size="small"
                      sx={{
                        width: { xs: 44, sm: 40 },
                        height: { xs: 44, sm: 40 },
                        flexShrink: 0,
                        borderRadius: '10px',
                        bgcolor: 'transparent',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(154, 161, 175, 0.2)' },
                      }}
                    >
                      {quickAction.loading ? (
                        <CircularProgress size={18} thickness={5} sx={{ color: '#99A1AF' }} />
                      ) : (
                        <QuestionnaireAddIcon
                          backFill="#B8BEC9"
                          frontFill="#99A1AF"
                          plusStroke="#fff"
                          size={28}
                        />
                      )}
                    </IconButton>
                  </Box>
                </Tooltip>
              )}
            </Box>

            {showDescription && description ? (
              <Typography
                sx={{
                  mt: 0.75,
                  ...listRowSubtitleSx,
                  display: isConditional ? '-webkit-box' : 'block',
                  WebkitLineClamp: isConditional ? 1 : 'unset',
                  WebkitBoxOrient: isConditional ? 'vertical' : 'unset',
                  overflow: isConditional ? 'hidden' : 'visible',
                }}
              >
                {description}
              </Typography>
            ) : null}

            <Box
              sx={{
                mt: 1.25,
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: { xs: 1.5, sm: 2.25 },
                rowGap: { xs: 1, sm: 1.25 },
                alignItems: 'center',
              }}
            >
              {shouldShowQuestionMetric && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Box component="img" src={notebookMetricIcon} alt="" sx={{ width: 18, height: 18, display: 'block' }} />
                  <Typography sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', color: '#667085', overflowWrap: 'anywhere' }}>
                    {getCardQuestionCount(card)} questions
                  </Typography>
                </Box>
              )}
              {shouldShowEstimatedTimeMetric && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Box component="img" src={clockMetricIcon} alt="" sx={{ width: 18, height: 18, display: 'block' }} />
                  <Typography sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', color: '#667085', overflowWrap: 'anywhere' }}>
                    {isConditional && getCardEstimatedMinutes(card) > 0
                      ? `${getCardEstimatedMinutes(card)} min`
                      : getListTimeLabel(card)}
                  </Typography>
                </Box>
              )}
              {!isConditional && showAssignedMetric && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Box component="img" src={assignedMetricIcon} alt="" sx={{ width: 18, height: 18, display: 'block' }} />
                  <Typography sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', color: '#667085', overflowWrap: 'anywhere' }}>
                    {assignedCount} assigned
                  </Typography>
                </Box>
              )}
              {showResponsesMetric && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Box component="img" src={responsesMetricIcon} alt="" sx={{ width: 18, height: 18, display: 'block' }} />
                  <Typography sx={{ fontFamily: 'Arial, sans-serif', fontSize: '15px', color: '#667085', overflowWrap: 'anywhere' }}>
                    {responseCount} responses
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1.5,
            width: { xs: '100%', lg: 'auto' },
            minWidth: 0,
            flexShrink: 0,
            flexWrap: { sm: 'wrap', lg: 'nowrap' },
            justifyContent: { xs: 'stretch', sm: 'flex-start', lg: 'flex-end' },
          }}
        >
          {showPreviewButton && (
            <Button
              data-action-tour-id="questionnaires-preview"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                onPreview();
              }}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                height: { xs: 44, sm: 38 },
                minWidth: { xs: 0, sm: 106 },
                minHeight: { xs: 44, sm: 38 },
                borderRadius: '10px',
                border: '1px solid #d6dee6',
                color: '#344054',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: '13px',
                '&:hover': { borderColor: '#b9c7d3', bgcolor: '#f8fafc' },
              }}
            >
              {previewLabel}
            </Button>
          )}
          {showResponsesButton && (
            <Button
              data-action-tour-id="questionnaires-responses"
              variant={responsesButtonVariant}
              disabled={responsesDisabled}
              onClick={(event) => {
                event.stopPropagation();
                onResponses();
              }}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                height: { xs: 44, sm: 38 },
                minWidth: { xs: 0, sm: 106 },
                minHeight: { xs: 44, sm: 38 },
                borderRadius: '10px',
                border: responsesButtonIsContained ? '1px solid transparent' : '1px solid transparent',
                bgcolor: responsesButtonIsContained ? '#20AEDC' : '#20AEDC',
                color: responsesButtonIsContained ? '#fff' : '#fff',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: '13px',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
                '&.Mui-disabled': {
                  bgcolor: 'transparent',
                  border: '1px solid #d6dee6',
                  color: '#d6dee6',
                },
              }}
            >
              {responsesLabel}
            </Button>
          )}
          {showFillManuallyButton && onFillManually && (
            <Button
              data-action-tour-id="questionnaires-fill-manually"
              variant="outlined"
              disabled={fillManuallyDisabled}
              onClick={(event) => {
                event.stopPropagation();
                onFillManually();
              }}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                height: { xs: 44, sm: 38 },
                minWidth: { xs: 0, sm: 126 },
                minHeight: { xs: 44, sm: 38 },
                borderRadius: '10px',
                border: '1px solid #d6dee6',
                bgcolor: '#fff',
                color: '#344054',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: '13px',
                '&:hover': { borderColor: '#b9c7d3', bgcolor: '#f8fafc' },
                '&.Mui-disabled': {
                  bgcolor: 'transparent',
                  border: '1px solid #d6dee6',
                  color: '#d6dee6',
                },
              }}
            >
              {fillManuallyLabel}
            </Button>
          )}
          {!isConditional && showAssignButton && (
            <Button
              data-action-tour-id="questionnaires-assign"
              variant={assignButtonVariant}
              disabled={assignDisabled}
              onClick={(event) => {
                event.stopPropagation();
                onAssign();
              }}
              endIcon={assignEndIcon}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                height: { xs: 44, sm: 38 },
                minWidth: { xs: 0, sm: 118 },
                minHeight: { xs: 44, sm: 38 },
                borderRadius: '10px',
                border: assignButtonIsContained ? '1px solid transparent' : '1px solid #d6dee6',
                bgcolor: assignButtonIsContained ? '#25a8da' : '#fff',
                color: assignButtonIsContained ? '#fff' : '#344054',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: '13px',
                '&:hover': assignButtonIsContained
                  ? { bgcolor: '#1498ca' }
                  : { borderColor: '#b9c7d3', bgcolor: '#f8fafc' },
                '&.Mui-disabled': assignButtonIsContained
                  ? {
                      border: '1px solid transparent',
                      bgcolor: '#9fd7ea',
                      color: '#ffffff',
                    }
                  : {
                      border: '1px solid #d6dee6',
                      bgcolor: '#f8fafc',
                      color: '#475467',
                    },
              }}
            >
              {assignLabel}
            </Button>
          )}
          {showDeleteAction && (onEdit || onDelete) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                gap: 0.25,
                width: { xs: '100%', sm: 'auto' },
                flexShrink: 0,
                pt: { xs: 0.5, sm: 0 },
                mt: { xs: 0.5, sm: 0 },
                borderTop: { xs: '1px solid #eaecf0', sm: 'none' },
                pl: { sm: 0.5 },
                ml: { sm: 0.5 },
                borderLeft: { xs: 'none', sm: '1px solid #eaecf0' },
              }}
            >
              {onEdit && (
                <Tooltip title="Edit questionnaire">
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      onEdit();
                    }}
                    aria-label="Edit questionnaire"
                    sx={{
                      width: 38,
                      height: 38,
                      p: 0.75,
                      borderRadius: '10px',
                      '&:hover': {
                        bgcolor: 'rgba(153, 161, 175, 0.1)',
                      },
                    }}
                  >
                    <Box component="img" src={editIcon} alt="" sx={{ width: 18, height: 18, display: 'block' }} />
                  </IconButton>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip title="Delete questionnaire">
                  <IconButton
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete();
                    }}
                    aria-label="Delete questionnaire"
                    sx={{
                      width: 38,
                      height: 38,
                      p: 0.75,
                      borderRadius: '10px',
                      '&:hover': {
                        bgcolor: 'rgba(249, 112, 102, 0.08)',
                      },
                    }}
                  >
                    <Box component="img" src={deleteIcon} alt="" sx={{ width: 18, height: 18, display: 'block' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
